import React, { useState } from 'react';
import { QAWidgetConfig } from '../../../../../types';

interface QAWidgetBuilderProps {
  config: Partial<QAWidgetConfig>;
  onChange: (config: QAWidgetConfig) => void;
}

const QAWidgetBuilder: React.FC<QAWidgetBuilderProps> = ({
  config,
  onChange,
}) => {
  const [localConfig, setLocalConfig] = useState<QAWidgetConfig>({
    layout: config.layout || 'vertical',
    showQuestionNumbers: config.showQuestionNumbers !== false,
    allowAnswerEditing: config.allowAnswerEditing ?? true,
    styling: config.styling || {},
  });

  const updateConfig = (updates: Partial<QAWidgetConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  return (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
        <strong>Q&A Widget</strong> - A generic question/answer form widget.
        <p className="mt-1 text-purple-600">
          The actual questions and data key are configured when you add this widget to a dashboard.
          These settings are just defaults.
        </p>
      </div>

      {/* Default Layout */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Default Layout Style
        </label>
        <select
          value={localConfig.layout}
          onChange={(e) => updateConfig({ layout: e.target.value as 'vertical' | 'horizontal' | 'accordion' })}
          className="w-full px-3 py-2 border rounded-lg"
        >
          <option value="vertical">Vertical (stacked)</option>
          <option value="horizontal">Horizontal (side by side)</option>
          <option value="accordion">Accordion (collapsible)</option>
        </select>
      </div>

      {/* Default Options */}
      <div className="border rounded-lg p-4 bg-neutral-50">
        <h4 className="font-medium text-neutral-700 mb-3">Default Options</h4>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.showQuestionNumbers}
              onChange={(e) => updateConfig({ showQuestionNumbers: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-purple-600"
            />
            <span className="text-sm text-neutral-700">Show question numbers</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.allowAnswerEditing}
              onChange={(e) => updateConfig({ allowAnswerEditing: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-purple-600"
            />
            <span className="text-sm text-neutral-700">Allow answer editing by default</span>
          </label>
        </div>
      </div>

      {/* Styling Info */}
      <div className="border rounded-lg p-4 bg-neutral-50">
        <h4 className="font-medium text-neutral-700 mb-3">Styling</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ backgroundColor: '#2563eb' }} />
            <span className="text-sm text-neutral-600">Questions: Blue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ backgroundColor: '#000000' }} />
            <span className="text-sm text-neutral-600">Answers: Black</span>
          </div>
        </div>
      </div>

      <div className="text-xs text-neutral-500 text-center p-2 bg-neutral-50 rounded">
        These are template defaults. When adding this widget to a dashboard,
        you'll specify the data key and can override these settings.
      </div>
    </div>
  );
};

export default QAWidgetBuilder;
